import { inject, Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { DecryptionError } from '../errors/decryption.error';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  readonly storageService = inject(StorageService);

  encrypt = (encryptionKey: string, value: string, storageKey?: string) => {
    let encryptedData: string;
    if (storageKey) {
      encryptedData = CryptoJS.AES.encrypt(value, `${encryptionKey}-${storageKey}`).toString();
    } else {
      encryptedData = CryptoJS.AES.encrypt(value, encryptionKey).toString();
    }
    return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(encryptedData));
  };

  decrypt = (encryptionKey: string, token: string | null, storageKey?: string): string => {
    if (!token) {
      return '';
    }

    try {
      const decryptedJson = CryptoJS.enc.Base64.parse(token).toString(CryptoJS.enc.Utf8);
      const decryptedToken = storageKey
        ? CryptoJS.AES.decrypt(decryptedJson, `${encryptionKey}-${storageKey}`)
        : CryptoJS.AES.decrypt(decryptedJson, encryptionKey);

      // Check if decryption produced valid bytes
      if (!decryptedToken || decryptedToken.sigBytes === 0) {
        throw new DecryptionError(
          'Decryption failed: Invalid ciphertext or encryption key mismatch',
          storageKey,
        );
      }

      const result = decryptedToken.toString(CryptoJS.enc.Utf8);

      // Additional validation: ensure the decrypted data is valid UTF-8
      if (!result || result.trim() === '') {
        throw new DecryptionError(
          'Decryption failed: Result is empty or invalid UTF-8',
          storageKey,
        );
      }

      return result;
    } catch (error) {
      // If it's already a DecryptionError, re-throw it
      if (error instanceof DecryptionError) {
        throw error;
      }

      // Handle other decryption errors (malformed data, invalid base64, etc.)
      throw new DecryptionError(
        'Decryption failed: Corrupted data or invalid format',
        storageKey,
        error,
      );
    }
  };
}
